import React, { useEffect, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import Navigation from './Navigation'
import { useAuthStore } from '../../stores/authStore'
import { api } from '../../services/api'

// App chrome. Desktop (lg+): persistent 16rem sidebar + content column.
// Mobile/tablet (<lg): single column with a top bar; the sidebar becomes a
// slide-over drawer opened by the hamburger button. The drawer closes on
// backdrop tap, Escape, or any navigation.
export default function AppShell() {
  const currentPerson = useAuthStore((s) => s.currentPerson)
  const clearSession = useAuthStore((s) => s.clearSession)
  const location = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(false)

  // Close the drawer whenever the route changes (a nav link was tapped).
  useEffect(() => {
    setSidebarOpen(false)
  }, [location.pathname])

  // Escape closes the drawer.
  useEffect(() => {
    if (!sidebarOpen) return undefined
    const onKey = (e) => {
      if (e.key === 'Escape') setSidebarOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [sidebarOpen])

  // Lock body scroll while the drawer is open (mobile only in practice —
  // the drawer can't be open on lg+ where the backdrop/transform are inert).
  useEffect(() => {
    if (!sidebarOpen) return undefined
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [sidebarOpen])

  const handleSignOut = async () => {
    try {
      // Devise session sign-out (Phase 1.5 — JWT auth is gone).
      await api.delete('/users/sign_out')
    } catch {
      // fall through — we still clear local state
    }
    clearSession()
    // Full page load, NOT a client-side navigate: Devise's reset_session
    // rotates the CSRF token, and Login.jsx posts the token from the
    // server-rendered meta tag. After an SPA navigate that tag is stale and
    // the OmniAuth POST is rejected until a hard refresh. (Same approach as
    // the 401 handler in services/api.js.)
    window.location.assign('/login')
  }

  const brand = (
    <span className="text-lg font-semibold tracking-tight text-slate-900">
      kyu <span className="text-accent-600">Initiative Signals</span>
    </span>
  )

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 lg:flex">
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-slate-900/40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar: slide-over drawer below lg, static column at lg+ */}
      <aside
        id="app-sidebar"
        aria-label="Main navigation"
        className={[
          'fixed inset-y-0 left-0 z-50 flex w-64 max-w-[85vw] flex-col border-r border-slate-200 bg-white',
          'transform transition-transform duration-200 ease-in-out',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full',
          'lg:static lg:z-auto lg:max-w-none lg:translate-x-0 lg:shrink-0 lg:transition-none',
        ].join(' ')}
      >
        <div className="flex h-16 items-center justify-between border-b border-slate-200 px-6">
          {brand}
          <button
            type="button"
            onClick={() => setSidebarOpen(false)}
            aria-label="Close menu"
            className="lg:hidden -mr-2 rounded-md p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <Navigation />
        </div>

        <div className="border-t border-slate-200 p-4">
          <div className="mb-2 text-xs uppercase tracking-wide text-slate-500">Signed in as</div>
          <div className="truncate text-sm font-medium text-slate-900">
            {currentPerson?.full_name || currentPerson?.email || '—'}
          </div>
          <div className="mb-3 truncate text-xs text-slate-500">{currentPerson?.email}</div>
          <button
            type="button"
            onClick={handleSignOut}
            className="w-full rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <div className="flex h-16 items-center gap-3 border-b border-slate-200 bg-white px-4 sm:px-6 lg:px-8">
          {/* Hamburger — mobile/tablet only */}
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open menu"
            aria-expanded={sidebarOpen}
            aria-controls="app-sidebar"
            className="-ml-1 rounded-md p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-700 lg:hidden"
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>

          {/* Brand in the top bar where the sidebar is hidden */}
          <div className="truncate lg:hidden">{brand}</div>
        </div>

        <div className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  )
}
